import React, { useEffect, useState, useRef, useLayoutEffect, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import './AssetSummaryDetails.css';
import { getAssetAlerts, AssetDetailsResponse, AssetAttribute, AssetAlert } from '../../Services/serviceURL';
import apiInstance from '../../config/HttpCommon';
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import * as am5radar from "@amcharts/amcharts5/radar";
import am5themes_Dark from "@amcharts/amcharts5/themes/Dark";
import AnalysisTab from './AnalysisTab';
import { createAmChartsRoot } from '../../utils/amChartsConfig';
import RemoteAccess from './RemoteAccess';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Loader from '../../Components/common/loader/loader';
import { formatAttributeValue, formatDateTime, getIsAdminUser } from './assetSummaryDetailsHelpers';
import { useAssetSummaryFilters } from './useAssetSummaryFilters';
import AssetSummaryFilterDropdowns from './AssetSummaryFilterDropdowns';
import AssetSummaryDocumentsSection from './AssetSummaryDocumentsSection';
import { fetchConnectivityChartData } from './connectivityTrendData';

interface AssetSummaryDetailsProps {
  assetId?: string;
}

// Use the imported types from serviceURL
type AssetDetails = AssetDetailsResponse;

const allTabs = [
  { id: 'summary', label: 'Summary' },
  { id: 'analysis', label: 'Analysis' },
  { id: 'documents', label: 'Documents' },
  { id: 'remote', label: 'Remote' },
];

const AssetSummaryDetails: React.FC<AssetSummaryDetailsProps> = ({ assetId: propAssetId }) => {
  const { assetId: urlAssetId } = useParams<{ assetId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const assetId = propAssetId || urlAssetId;

  const filters = useAssetSummaryFilters({ assetId, navigate, searchParams });
  const isAdmin = useMemo(() => getIsAdminUser(), []);
  const canViewConnectivityTrend = isAdmin;
  const tabs = useMemo(() => allTabs.filter((tab) => tab.id !== 'remote' || isAdmin), [isAdmin]);

  const [activeTab, setActiveTab] = useState<string>('summary');
  const [remoteKey, setRemoteKey] = useState(0);
  const analysisFirstLoadRef = useRef(false);
  const [triggerAnalysisInitialLoad, setTriggerAnalysisInitialLoad] = useState(false);

  const onClickTab = (tabId: string) => {
    if (tabId === 'remote' && !isAdmin) return;
    setActiveTab(tabId);
    if (tabId === 'analysis' && !analysisFirstLoadRef.current) {
      analysisFirstLoadRef.current = true;
      setTriggerAnalysisInitialLoad(true);
    }
    if (tabId === 'remote') {
      setRemoteKey((prev) => prev + 1);
    }
  };

 return (
    <div className="asset-summary-details">
      <AssetSummaryFilterDropdowns
        customers={filters.customers}
        sites={filters.sites}
        plants={filters.plants}
        assets={filters.assets}
        selectedCustomerId={filters.selectedCustomerId}
        selectedSiteId={filters.selectedSiteId}
        selectedPlantId={filters.selectedPlantId}
        selectedAssetId={filters.selectedAssetId}
        handleCustomerChange={filters.handleCustomerChange}
        setSelectedSiteId={filters.setSelectedSiteId}
        setSelectedPlantId={filters.setSelectedPlantId}
        setSelectedAssetId={filters.setSelectedAssetId}
        setSelectedAssetRecordId={filters.setSelectedAssetRecordId}
        setAssetDetails={filters.setAssetDetails}
        setPlants={filters.setPlants}
        setAssets={filters.setAssets}
        refreshPlantsForSite={filters.refreshPlantsForSite}
        refreshAssetsForPlant={filters.refreshAssetsForPlant}
        navigate={filters.navigate}
      />

      <div className="tabs-container">
        <div className="tabs-header">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => onClickTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>
       <div className="tab-content-wrapper">

  {/* ================= SUMMARY TAB ================= */}
  <div
    className="tab-content"
    style={{ display: activeTab === 'summary' ? 'block' : 'none' }}
  >
    <div className={`widgets-container ${!canViewConnectivityTrend ? "no-connectivity" : ""}`}>
      <InfoWidget assetDetails={filters.assetDetails} loading={filters.loadingAssetDetails} />

      <AssetPropertiesWidget
        assetDetails={filters.assetDetails}
        loading={filters.loadingAssetDetails}
        formatAttributeValue={formatAttributeValue}
        lastCommunication={filters.lastCommunication}
      />

      <SensorStatusWidget
        assetDetails={filters.assetDetails}
        loading={filters.loadingAssetDetails}
      />

      <AlertsWidget
        assetId={filters.selectedAssetRecordId}
        assetDetails={filters.assetDetails}
        loading={filters.loadingAssetDetails}
      />

    {canViewConnectivityTrend && (
    <ConnectivityTrendWidget
      assetId={filters.selectedAssetId}
      assetDetails={filters.assetDetails}
    />
    )}
    </div>
  </div>

  {/* ================= ANALYSIS TAB ================= */}
  <div
    className="tab-content"
    style={{ display: activeTab === 'analysis' ? 'block' : 'none' }}
  >
    <AnalysisTab
      assetId={assetId}
      assetType={filters.assetDetails?.assetType}
      assetParameters={filters.assetDetails?.attributes}
      triggerInitialLoad={triggerAnalysisInitialLoad}
    />
  </div>

  {/* ================= ALERTS TAB ================= */}
  <div
    className="tab-content"
    style={{ display: activeTab === 'alerts' ? 'block' : 'none' }}
  >
    <div className="alerts-content">
      <h3>Active Alerts</h3>
    </div>
  </div>

  <AssetSummaryDocumentsSection
    assetId={assetId}
    selectedAssetId={filters.selectedAssetId}
    selectedAssetRecordId={filters.selectedAssetRecordId}
    activeTab={activeTab}
  />

  {/* ================= NOTES TAB ================= */}
  <div
    className="tab-content"
    style={{ display: activeTab === 'notes' ? 'block' : 'none' }}
  >
    <div className="notes-content">
      <h3>Notes & Comments</h3>
    </div>
  </div>

  {/* ================= REMOTE TAB ================= */}
  {isAdmin && (
    <div
      className="tab-content"
      style={{ display: activeTab === 'remote' ? 'block' : 'none' }}
    >
      <RemoteAccess 
        key={`remote-${remoteKey}`}
        selectedAssetRecordId={filters.selectedAssetRecordId}
      />
    </div>
  )}
</div>

      </div>

      {/* Toast Container */}
      <ToastContainer
        className="toastify-asset-summary"
        position="top-right"
        autoClose={2000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        style={{ top: '80px', right: '20px', zIndex: 10002 }}
      />
    </div>
  );
};

// Info Widget Component
const InfoWidget: React.FC<{ assetDetails: AssetDetails | null; loading: boolean }> = ({ assetDetails, loading }) => {
  if (loading) {
    return (
      <div className="widget info-widget">
        <div className="widget-header">
          <h3>Info</h3>
        </div>
        <div className='loading-container'>
          <Loader/>
        </div>
      </div>
    );
  }

  return (
    <div className="widget info-widget">
      <div className="widget-header">
        <h3>Info</h3>
      </div>
      <div className="widget-content-info">
        {assetDetails ? (
          <div className="info-grid">
            <div className="info-item">
              <span className="label">Plant Name:</span>
              <span className="value">{assetDetails.plant || 'N/A'}</span>
            </div>
            <div className="info-item">
              <span className="label">Asset Type:</span>
              <span className="value">{assetDetails.assetType || 'N/A'}</span>
            </div>
            <div className="info-item">
              <span className="label">Asset Unique ID/Name:</span>
              <span className="value">{assetDetails.assetName || 'N/A'}</span>
            </div>
            <div className="info-item">
              <span className="label">Status:</span>
              <span className={`value ${assetDetails.status?.toLowerCase() === 'active' ? 'status-active' : ''}`}>
                {assetDetails.status || 'N/A'}
              </span>
            </div>
            <div className="info-item">
              <span className="label">Gateway Status:</span>
              <span className={`value ${assetDetails.ioTDeviceConnectionState?.toLowerCase() === 'active' ? 'status-active' : ''}`}>
                {assetDetails.ioTDeviceConnectionState || 'N/A'}
              </span>
            </div>
            <div className="info-item">
              <span className="label">Gateway#:</span>
              <span className="value">{assetDetails.gatewayDeviceId || 'N/A'}</span>
            </div>
            <div className="info-item">
              <span className="label">Controller#:</span>
              <span className="value">{assetDetails.controllerDeviceId || 'N/A'}</span>
            </div>
            <div className="info-item">
              <span className="label">Last Data Received:</span>
              <span className="value">
                {formatDateTime(assetDetails.lastCommunication)}
              </span>
            </div>
            <div className="info-item">
              <span className="label">Commissioned on :</span>
              <span className="value">
                {formatDateTime(assetDetails.firstCommunication)}
              </span>
            </div>
          </div>
        ) : (
          <div className="info-item">
            <span className="label">No info available</span>
          </div>
        )}
      </div>
    </div>
  );
};

// Shared helper function to dynamically determine if a sensor is relay-related
const isRelaySensor = (attribute: any): boolean => {
  if (!attribute) return false;
  
  // Get both sensor name fields for checking
  const sensorName = (attribute.sensorName || '').toLowerCase();
  const displayName = (attribute.name || '').toLowerCase();
  
  // First, exclude known measurement sensors explicitly by checking both name fields
  const measurementSensorNames = [
    'ph', 'temperature', 'conductivity', 'orp', 'dissolved oxygen', 'turbidity',
    'fluorescein', 'sulfite', 'chlorine', 'pressure', 'flow rate', 'level',
    'user custom', 'custom', 'water meter'
  ];
  
  const isMeasurementSensor = measurementSensorNames.some(name => 
    sensorName.includes(name) || displayName.includes(name) || 
    displayName.startsWith(name) || sensorName.startsWith(name)
  );
  
  if (isMeasurementSensor) return false;
  
  // Primary criteria: Check for explicit relay-related naming patterns
  const relayKeywords = [
    'relay', 'pump', 'valve', 'switch', 'motor', 'heater', 'cooler',
    'blower', 'feeder', 'dosing', 'chemical', 'alarm', 'led', 'light',
    'solenoid', 'modem', 'power pack'
  ];
  
  const hasRelayNaming = relayKeywords.some(keyword => 
    sensorName.includes(keyword) || displayName.includes(keyword)
  );
  
  // For explicit relay naming, return true immediately
  if (hasRelayNaming) return true;
  
  // Secondary criteria: Must have position starting with 'R' (Relay position) or 'D' (Digital position)
  const position = (attribute.position || '').toUpperCase();
  const isRelayPosition = position.startsWith('R') || position.startsWith('D');
  
  // Check for binary control characteristics
  const hasBinaryValue = (attribute.value === 0 || attribute.value === 1) && !isNaN(Number(attribute.value));
  const hasControlSetpoint = attribute.setpoint !== null && attribute.setpoint !== undefined && attribute.setpoint !== 'N/A';
  
  // Must have relay position AND binary characteristics for sensors without explicit relay names
  return isRelayPosition && hasBinaryValue;
};

const AssetPropertiesWidget: React.FC<{
  assetDetails: AssetDetails | null;
  loading: boolean;
  formatAttributeValue: (value: number | null | undefined) => string;
  lastCommunication?: string | null;
}> = ({ assetDetails, loading, formatAttributeValue, lastCommunication }) => {

  // Helper function to check if sensor contains "imbalance" keyword
  const hasImbalanceKeyword = (attribute: AssetAttribute): boolean => {
    return attribute.name.toLowerCase().includes('imbalance');
  };

  // Filter out relay-related sensors and imbalance sensors from the properties display
  const nonRelayAttributes = assetDetails?.attributes?.filter(attr => !isRelaySensor(attr) && !hasImbalanceKeyword(attr)) || [];

  // Helper function to clean sensor names by removing "(Imbalance)"
  const cleanSensorName = (name: string): string => {
    return name.replace(/\s*\(Imbalance\)$/i, '');
  };

  return (
    <div className="widget asset-properties-widget">

      {/* TITLE */}
      <div className="widget-header">
        <h3>
          Properties
          {lastCommunication ? ` (${formatDateTime(lastCommunication)})` : ''}
        </h3>
      </div>

      
      <div className="properties-table-header">
        <div className="col sensor">Sensor</div>
        <div className="col reading">Last Reading</div>
        <div className="col alarm-l">Alarm L</div>
        <div className="col alarm-h">Alarm H</div>
      </div>

      {/* CONTENT */}
      <div className="widget-content">

        {loading ? (
          <div className="loading-container">
            <Loader />
          </div>
        ) : nonRelayAttributes.length ? (

          nonRelayAttributes.map((attribute, index) => (
            <div key={index} className="properties-row">
              <div className="label">{cleanSensorName(attribute.name)}</div>

              <div className="value">
                {formatAttributeValue(attribute.value)}
                {attribute.value != null && attribute.unit ? `\u00A0${attribute.unit}` : ''}
              </div>

              <div className="value">{attribute.thresholdL ?? '—'}</div>
              <div className="value">{attribute.thresholdH ?? '—'}</div>
            </div>
          ))

        ) : (
          <div className="properties-row">
            <div>No attributes available</div>
            <div></div>
            <div></div>
            <div></div>
          </div>
        )}

      </div>
    </div>
  );
};


// Sensor Status Widget Component
const SensorStatusWidget: React.FC<{ assetDetails: AssetDetails | null; loading: boolean }> = ({ assetDetails, loading }) => {
  if (loading) {
    return (
      <div className="widget sensor-status-widget">
        <div className="widget-header">
          <h3>Relay Status</h3>
        </div>
        
        <div className='loading-container'>
          <Loader/>
        </div>
      </div>
    );
  }

  // Helper function to check if sensor contains "imbalance" keyword
  const hasImbalanceKeyword = (attribute: AssetAttribute): boolean => {
    return attribute.name.toLowerCase().includes('imbalance');
  };

  // Filter attributes to find relay-related items using shared function, excluding imbalance sensors
  const relayAttributes = assetDetails?.attributes?.filter(attr => isRelaySensor(attr) && !hasImbalanceKeyword(attr)) || [];

  return (
  <div className="widget sensor-status-widget">

    {/* TITLE */}
    <div className="widget-header">
      <h3>Relay Status</h3>
    </div>

    
    <div className="relay-table-header">
      <div className="col relay">Relay Label</div>
      <div className="col mode">Mode</div>
      <div className="col point">Set Point</div>
    </div>

    {/* CONTENT */}
    <div className="widget-content">

      {relayAttributes.length > 0 ? (
        relayAttributes.map((relay, index) => (
          <div key={index} className="relay-row">

            <div className="label">{relay.name}</div>

            <div className="value">
  <span
    className={`relay-status ${relay.value === 1 ? 'on' : 'off'}`}
  >
    {relay.value === 1 ? 'ON' : 'OFF'}
  </span>
</div>


            <div className="value">
              {relay.setpoint ?? '—'}
            </div>

          </div>
        ))
      ) : (
        <div className="relay-row">
          <div className="label">
            {assetDetails ? 'No relay information available' : 'No asset selected'}
          </div>
          <div></div>
          <div></div>
        </div>
      )}

    </div>
  </div>
);

};

// Optional: Recent Alerts Widget using API data
const RecentAlertsWidget: React.FC<{ assetDetails: AssetDetails | null; loading: boolean }> = ({ assetDetails, loading }) => {
  if (loading) {
    return (
      <div className="widget recent-alerts-widget">
        <div className="widget-header">
          <h3>Recent Alerts</h3>
        </div>
        <div className="widget-content">
          <div className="loading-spinner">Loading...</div>
        </div>
      </div>
    );
  }

  const formatAlertTime = (alertTime: string): string => {
    try {
      const date = new Date(alertTime);
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return alertTime;
    }
  };

  const getSeverityClass = (severity: string): string => {
    const severityLower = severity?.toLowerCase() || '';
    switch (severityLower) {
      case 'critical':
      case 'high':
        return 'severity-critical';
      case 'warning':
      case 'medium':
        return 'severity-warning';
      case 'info':
      case 'low':
        return 'severity-info';
      default:
        return 'severity-info';
    }
  };

  return (
    <div className="widget recent-alerts-widget">
      <div className="widget-header">
        <h3>Recent Alerts</h3>
      </div>
      <div className="widget-content">
        {assetDetails?.alerts && assetDetails.alerts.length > 0 ? (
          <div className="alerts-list">
            {assetDetails.alerts.slice(0, 5).map((alert, index) => (
              <div key={index} className="alert-item">
                <div className="alert-info">
                  <span className="alert-reason">{alert.reason || 'Unknown'}</span>
                  <span className="alert-time">{formatAlertTime(alert.alertTime)}</span>
                </div>
                <span className={`alert-severity ${getSeverityClass(alert.severity)}`}>
                  {alert.severity || 'Unknown'}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <div className="no-alerts">
            <span>No recent alerts</span>
          </div>
        )}
      </div>
    </div>
  );
};

// Alerts Widget Component
interface AlertsWidgetProps {
  assetId?: string;
  assetDetails?: AssetDetails | null;
  loading?: boolean;
}

const AlertsWidget: React.FC<AlertsWidgetProps> = ({ assetId, assetDetails, loading }) => {
  const [selectedTimeRange, setSelectedTimeRange] = useState<string>('24 hours');
  const [showCalendarModal, setShowCalendarModal] = useState<boolean>(false);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date());
  const [selectingStart, setSelectingStart] = useState<boolean>(true);
  const [filteredAlerts, setFilteredAlerts] = useState<AssetAlert[]>([]);
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [apiAlerts, setApiAlerts] = useState<AssetAlert[]>([]);
  const [loadingAlerts, setLoadingAlerts] = useState<boolean>(false);

  const calendarModalRef = useRef<HTMLDivElement>(null);
  const lastApiCallRef = useRef<string>(''); // Track last API call to prevent duplicates

  const timeRangeOptions = [
    '24 hours',
    '7 days',
    'Select Date Range'
  ];

  // Function to format date for API (YYYY-MM-DD format)
  const formatDateForAPI = (date: Date): string => {
    return date.toISOString().split('T')[0];
  };

  // Function to format date for display (DD/MM/YYYY format)
  const formatDateForDisplay = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Function to get severity class for styling
  const getSeverityClass = (severity: string): string => {
    if (!severity) return 'unknown';
    const severityLower = severity.toLowerCase();
    switch (severityLower) {
      case 'critical':
      case 'high':
      case 'danger':
        return 'critical';
      case 'medium':
      case 'warning':
        return 'warning';
      case 'low':
      case 'info':
        return 'info';
      case 'failed':
      case 'error':
        return 'failed';
      default:
        return 'unknown';
    }
  };

  // Calendar modal handlers (following AnalysisTab pattern)
  const handleCalendarToggle = () => {
    setShowCalendarModal(!showCalendarModal);
  };

  const handleTimeRangeSelect = (range: string) => {
    setSelectedTimeRange(range);
    if (range !== 'Select Date Range') {
      setShowCalendarModal(false);
      // Filter alerts for predefined ranges
      filterAlerts(range);
    } else {
      // Open calendar modal for custom date range
      setShowCalendarModal(true);
    }
  };

  const handleDateRangeApply = () => {
    if (startDate && endDate) {
      setShowCalendarModal(false);
      setSelectedTimeRange('Select Date Range');
    }
  };

  const handleDateClick = (date: Date) => {
    if (selectingStart || !startDate) {
      setStartDate(date);
      setSelectingStart(false);
    } else {
      if (date < startDate) {
        setStartDate(date);
        setEndDate(startDate);
      } else {
        setEndDate(date);
      }
      setSelectingStart(true);
    }
  };

  const isDateInRange = (date: Date): boolean => {
    if (!startDate || !endDate) return false;
    return date >= startDate && date <= endDate;
  };

  const isStartDate = (date: Date): boolean => {
    return startDate ? date.toDateString() === startDate.toDateString() : false;
  };

  const isEndDate = (date: Date): boolean => {
    return endDate ? date.toDateString() === endDate.toDateString() : false;
  };

  const getDaysInMonth = (date: Date): Date[] => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());

    const days: Date[] = [];
    for (let i = 0; i < 42; i++) {
      days.push(new Date(startDate));
      startDate.setDate(startDate.getDate() + 1);
    }
    return days;
  };

  const navigateMonth = (direction: number) => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + direction, 1));
  };

  const resetDateSelection = () => {
    setStartDate(null);
    setEndDate(null);
    setSelectingStart(true);
  };

// In the fetchAlertsFromAPI function (around line 2040)
const fetchAlertsFromAPI = async (startDateStr: string, endDateStr: string) => {
  if (!assetId) {
    return;
  }

  // Create a unique key for this API call to prevent duplicates
  const apiCallKey = `${assetId}-${startDateStr}-${endDateStr}`;
  if (lastApiCallRef.current === apiCallKey) {
    return;
  }

  lastApiCallRef.current = apiCallKey;
  setLoadingAlerts(true);
  
  try {
    // Get user's timezone from preferences or use browser timezone
    const savedPreferences = localStorage.getItem('userPreferences');
    let userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    
    if (savedPreferences) {
      try {
        const prefs = JSON.parse(savedPreferences);
        if (prefs.timeZone) {
          userTimezone = prefs.timeZone;
        }
      } catch (e) {
      }
    }
       const getRelativeTimerange = (): number => {
      switch (selectedTimeRange) {
        case '24 hours':
          return 4;
        case '7 days':
          return 5;
        default:
          return 6; // Custom date range
      }
    };
    const timeRange= getRelativeTimerange();

    // Call getAssetAlerts with timezone parameter
    const alerts = await getAssetAlerts(assetId, startDateStr, endDateStr,timeRange);
    setApiAlerts(alerts);
    setFilteredAlerts(alerts);
  } catch (error) {
    setApiAlerts([]);
    setFilteredAlerts([]);
  } finally {
    setLoadingAlerts(false);
  }
};

  // Function to filter alerts based on time range
  const filterAlerts = (range: string) => {
    const now = new Date();
    let startTime: Date;
    let endTime: Date = now;

    switch (range) {
      case '24 hours':
        startTime = new Date(now.getTime() - 24 * 60 * 60 * 1000);
        break;
      case '7 days':
        startTime = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      default:
        // Default to 24 hours for any other case
        startTime = new Date(now.getTime() - 24 * 60 * 60 * 1000);
        break;
    }

    // Format dates for API call (YYYY-MM-DD)
    const startDateStr = startTime.toISOString().split('T')[0];
    const endDateStr = endTime.toISOString().split('T')[0];

    // Fetch alerts from API for the selected range
    fetchAlertsFromAPI(startDateStr, endDateStr);
  };

  // Filter alerts when assetDetails or time range changes
  useEffect(() => {
    // Reset API call tracker when assetId changes
    lastApiCallRef.current = '';

    if (assetId && selectedTimeRange !== 'Select Date Range') {
      // Only trigger for predefined ranges, not custom date ranges
      filterAlerts(selectedTimeRange);
    } else if (assetDetails?.alerts) {
      // Fallback to alerts from assetDetails if no assetId
      setFilteredAlerts(assetDetails.alerts);
    }
  }, [assetDetails, selectedTimeRange, assetId]);

  // Handle custom date range separately to avoid duplicate calls
  useEffect(() => {
    if (selectedTimeRange === 'Select Date Range' && startDate && endDate && assetId) {
      const startDateStr = startDate.toISOString().split('T')[0];
      const endDateStr = endDate.toISOString().split('T')[0];
      fetchAlertsFromAPI(startDateStr, endDateStr);
    }
  }, [startDate, endDate, assetId]);

   useEffect(() => {
  return () => {
    // ✅ SAFETY CLEANUP when component unmounts
    document.body.classList.remove('widget-expanded-body');
  };
}, []);

useLayoutEffect(() => {
  const handleEscKey = (event: KeyboardEvent) => {
    if (event.key === 'Escape' && isExpanded) {
      setIsExpanded(false);
      document.body.classList.remove('widget-expanded-body');
    }
  };

  if (isExpanded) {
    document.body.classList.add('widget-expanded-body');
    document.addEventListener('keydown', handleEscKey);
  }

  return () => {
    document.body.classList.remove('widget-expanded-body');
    document.removeEventListener('keydown', handleEscKey);
  };
}, [isExpanded]);



const handleExpandClick = () => {
  setIsExpanded(prev => {
    const next = !prev;

    if (next) {
      document.body.classList.add('widget-expanded-body');
    } else {
      document.body.classList.remove('widget-expanded-body');
    }

    return next;
  });
};
  // Close calendar modal when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        showCalendarModal &&
        calendarModalRef.current &&
        !calendarModalRef.current.contains(event.target as Node)
      ) {
        setShowCalendarModal(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCalendarModal]);

  return (
    <div className={`widget alerts-widget ${isExpanded ? 'expanded' : ''}`}>
         {isExpanded && (
          <button
            className='alerts-close'
            onClick={() => setIsExpanded(false)}
            title="Close"
            aria-label="Close fullscreen"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
              <path
                d="M18 6L6 18M6 6l12 12"
                stroke="red"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        )}
    <div className="widget-header">
        <div className="widget-title-row">
          <h3>Alerts <span className="alerts-badge">({filteredAlerts.length})</span></h3>
          <div className="widget-actions">
            <button
          className="widget-action-btn expand-btn"
          title={isExpanded ? 'Close' : 'Expand'}
          onClick={() => setIsExpanded(prev => !prev)}
          >
          
          {isExpanded ? (
            
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6l12 12"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            ) : (
              // ⛶ Expand icon
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3
                        m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </button>

          </div>
        </div>
        <div className="widget-controls-row">
          <div className="alerts-filter-buttons" style={{ position: 'relative' }}>
            {timeRangeOptions.map((option) => (
              <button
                key={option}
                className={`alert-filter-btn ${selectedTimeRange === option || (option === 'Select Date Range' && selectedTimeRange === 'Select Date Range' && startDate && endDate) ? 'active' : ''}`}
                onClick={() => handleTimeRangeSelect(option)}
                style={{
                  padding: '8px 16px',
                  margin: '0 4px',
                  background: selectedTimeRange === option || (option === 'Select Date Range' && selectedTimeRange === 'Select Date Range' && startDate && endDate) ? '#4a5463' : 'transparent',
                  color: '#e0e0e0',
                  border: '1px solid #4a5463',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                {option === 'Select Date Range' ? (
                  <>
                    {selectedTimeRange === 'Select Date Range' && startDate && endDate
                      ? `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`
                      : option}
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      style={{ marginLeft: '4px' }}
                    >
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" stroke="currentColor" strokeWidth="2" />
                      <line x1="16" y1="2" x2="16" y2="6" stroke="currentColor" strokeWidth="2" />
                      <line x1="8" y1="2" x2="8" y2="6" stroke="currentColor" strokeWidth="2" />
                      <line x1="3" y1="10" x2="21" y2="10" stroke="currentColor" strokeWidth="2" />
                    </svg>
                  </>
                ) : (
                  option
                )}
              </button>
            ))}

            {/* Calendar Modal - only shown when Select date range is clicked */}
            {showCalendarModal && (
              <div
                className='calendar-modal-container'
                ref={calendarModalRef}
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: '0',
                  right: 'auto',
                  background: '#22314a',
                  border: '1px solid #4a5463',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                  zIndex: 9999,
                  minWidth: '350px',
                  marginTop: '8px'
                }}
              >
                <div style={{ padding: '16px' }}>
                  <div className="calendar-section">
                    <div className="calendar-header" style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '12px'
                    }}>
                      <button
                        className="calendar-nav-btn"
                        onClick={() => navigateMonth(-1)}
                        style={{
                          background: 'transparent',
                          border: '1px solid #4a5463',
                          color: '#e0e0e0',
                          borderRadius: '4px',
                          width: '32px',
                          height: '32px',
                          cursor: 'pointer'
                        }}
                      >‹</button>
                      <span style={{ color: '#e0e0e0', fontSize: '14px', fontWeight: 'bold' }}>
                        {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                      </span>
                      <button
                        className="calendar-nav-btn"
                        onClick={() => navigateMonth(1)}
                        style={{
                          background: 'transparent',
                          border: '1px solid #4a5463',
                          color: '#e0e0e0',
                          borderRadius: '4px',
                          width: '32px',
                          height: '32px',
                          cursor: 'pointer'
                        }}
                      >›</button>
                    </div>

                    <div className="calendar-days-header" style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(7, 1fr)',
                      gap: '4px',
                      marginBottom: '8px'
                    }}>
                      {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                        <div key={day} style={{
                          padding: '8px 4px',
                          textAlign: 'center',
                          fontSize: '12px',
                          color: '#bfc9db',
                          fontWeight: 'bold'
                        }}>
                          {day}
                        </div>
                      ))}
                    </div>

                    <div className="calendar-days-grid" style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(7, 1fr)',
                      gap: '4px',
                      marginBottom: '12px'
                    }}>
                      {getDaysInMonth(currentMonth).map((date, index) => {
                        const isCurrentMonth = date.getMonth() === currentMonth.getMonth();
                        const isToday = date.toDateString() === new Date().toDateString();
                        const isStart = isStartDate(date);
                        const isEnd = isEndDate(date);
                        const isInRange = isDateInRange(date);

                        let dayStyle: React.CSSProperties = {
                          padding: '8px 4px',
                          textAlign: 'center',
                          fontSize: '12px',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: isCurrentMonth ? 'pointer' : 'default',
                          background: 'transparent',
                          color: isCurrentMonth ? '#e0e0e0' : '#666',
                        };

                        if (isToday && isCurrentMonth) {
                          dayStyle.background = 'rgba(74, 84, 99, 0.5)';
                          dayStyle.color = '#fff';
                        }

                        if (isStart || isEnd) {
                          dayStyle.background = '#4a5463';
                          dayStyle.color = '#fff';
                        } else if (isInRange && isCurrentMonth) {
                          dayStyle.background = 'rgba(74, 84, 99, 0.3)';
                          dayStyle.color = '#e0e0e0';
                        }

                        return (
                          <button
                            key={index}
                            style={dayStyle}
                            onClick={() => isCurrentMonth && handleDateClick(date)}
                            disabled={!isCurrentMonth}
                          >
                            {date.getDate()}
                          </button>
                        );
                      })}
                    </div>

                    {(startDate || endDate) && (
                      <div className="date-selection-info" style={{
                        marginTop: '12px',
                        padding: '8px',
                        background: 'rgba(74, 84, 99, 0.2)',
                        borderRadius: '4px'
                      }}>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          fontSize: '12px',
                          color: '#bfc9db'
                        }}>
                          <span>Start: {startDate ? startDate.toLocaleDateString() : 'Not selected'}</span>
                          <span>End: {endDate ? endDate.toLocaleDateString() : 'Not selected'}</span>
                        </div>
                      </div>
                    )}

                    <div className="calendar-footer" style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: '8px',
                      marginTop: '16px'
                    }}>
                      <button
                        className="reset-btn"
                        onClick={resetDateSelection}
                        style={{
                          background: 'transparent',
                          border: '1px solid #4a5463',
                          color: '#e0e0e0',
                          borderRadius: '4px',
                          padding: '8px 16px',
                          cursor: 'pointer',
                          fontSize: '12px'
                        }}
                      >
                        Reset
                      </button>
                      <button
                        className={`apply-btn ${(!startDate || !endDate) ? 'disabled' : ''}`}
                        onClick={handleDateRangeApply}
                        disabled={!startDate || !endDate}
                        style={{
                          background: (!startDate || !endDate) ? 'rgba(74, 84, 99, 0.5)' : '#4a5463',
                          border: '1px solid #4a5463',
                          color: (!startDate || !endDate) ? 'rgba(224, 224, 224, 0.5)' : '#e0e0e0',
                          borderRadius: '4px',
                          padding: '8px 16px',
                          cursor: (!startDate || !endDate) ? 'not-allowed' : 'pointer',
                          fontSize: '12px'
                        }}
                      >
                        Apply Custom Range
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="widget-content">
        {loadingAlerts ? (
          <div className="loading-state">Loading alerts...</div>
        ) : filteredAlerts.length === 0 ? (
          <div className="no-alerts-state">No alerts found for the selected time range.</div>
        ) : (
          <div className="alerts-table">
            {filteredAlerts.map((alert: AssetAlert, index: number) => (
              <div key={index} className="alert-row">
                <div className="alert-date-col">{formatDateForDisplay(alert.alertTime)}</div>
                <div className="alert-message-col">
                  <div className="alert-reason">{alert.reason}</div>
                  <div className="alert-attribute">
                    {alert.reason === "Device Communication Lost" 
                      ? alert.attribute 
                      : `${alert.attribute}: ${alert.value}`}
                  </div>
                </div>
                <div className="alert-status-col">
                  <span className={`alert-status ${getSeverityClass(alert.severity)}`}>
                    {alert.severity}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Connectivity Trend Widget Component
interface ConnectivityTrendWidgetProps {
  assetId: string;
  assetDetails: AssetDetails | null;
}

const ConnectivityTrendWidget: React.FC<ConnectivityTrendWidgetProps> = ({ assetId, assetDetails }) => {
  const chartDiv = useRef<HTMLDivElement>(null);
  const [selectedTimeRange, setSelectedTimeRange] = useState<string>('7 days');
  const [showCalendarModal, setShowCalendarModal] = useState<boolean>(false);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date());
  const [selectingStart, setSelectingStart] = useState<boolean>(true);
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [connectivityData, setConnectivityData] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const calendarModalRef = useRef<HTMLDivElement>(null);

  const timeRangeOptions = [
    '7 days',
    'Select Date Range'
  ];

  // Function to calculate date range based on selected option
  const getDateRange = (timeRange: string): { startTime: string, endTime: string } => {
    const now = new Date();
    const endTime = now.toISOString();
    let startTime: string;

    switch (timeRange) {
      case '7 days':
        startTime = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString();
        break;
      default:
        if (startDate && endDate) {
          startTime = startDate.toISOString();
          return { startTime, endTime: endDate.toISOString() };
        }
        // Default to 24 hours
        startTime = new Date(now.getTime() -7 * 24 * 60 * 60 * 1000).toISOString();
    }

    return { startTime, endTime };
  };

  const fetchConnectivityData = async (timeRange: string) => {
    if (!assetId) return;

    setLoading(true);
    try {
      const chartData = await fetchConnectivityChartData(assetId, timeRange, selectedTimeRange, getDateRange);
      setConnectivityData(chartData);
    } catch {
      setConnectivityData([]);
    } finally {
      setLoading(false);
    }
  };
  // Effect to fetch data when component mounts or timeRange/assetId changes
  useEffect(() => {
    if (assetDetails) { // Only fetch when assetDetails is available
      fetchConnectivityData(selectedTimeRange);
    }
  }, [selectedTimeRange, assetId, assetDetails]);

  // Handle time range change
  const handleTimeRangeSelect = (range: string) => {
    setSelectedTimeRange(range);
    if (range !== 'Select Date Range') {
      setShowCalendarModal(false);
      // Only update state, let useEffect handle fetch
    } else {
      // Open calendar modal for custom date range
      setShowCalendarModal(true);
    }
  };

  const handleCalendarToggle = () => {
    setShowCalendarModal(!showCalendarModal);
  };

  const handleDateRangeApply = () => {
    if (startDate && endDate) {
      setShowCalendarModal(false);
      setSelectedTimeRange('Select Date Range');
      // Fetch data for custom range
      fetchConnectivityData('custom');
    } else {
    }
  };

  const handleDateClick = (date: Date) => {
    if (selectingStart || !startDate) {
      setStartDate(date);
      setSelectingStart(false);
    } else {
      if (date < startDate) {
        setStartDate(date);
        setEndDate(startDate);
      } else {
        setEndDate(date);
      }
      setSelectingStart(true);
    }
  };

  const isDateInRange = (date: Date): boolean => {
    if (!startDate || !endDate) return false;
    return date >= startDate && date <= endDate;
  };

  const isStartDate = (date: Date): boolean => {
    return startDate ? date.toDateString() === startDate.toDateString() : false;
  };

  const isEndDate = (date: Date): boolean => {
    return endDate ? date.toDateString() === endDate.toDateString() : false;
  };

  const getDaysInMonth = (date: Date): Date[] => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days: Date[] = [];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(new Date(year, month, 1 - startingDayOfWeek + i));
    }

    // Add all days of the current month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }

    // Add days from next month to fill the calendar grid
    const remainingCells = 42 - days.length; // 6 rows × 7 days = 42 cells
    for (let day = 1; day <= remainingCells; day++) {
      days.push(new Date(year, month + 1, day));
    }

    return days;
  };

  const navigateMonth = (direction: number) => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + direction, 1));
  };

  const resetDateSelection = () => {
    setStartDate(null);
    setEndDate(null);
    setSelectingStart(true);
  };

  useLayoutEffect(() => {
    if (!chartDiv.current) return;

    // Create root element with license
    const root = createAmChartsRoot(chartDiv.current);

    // Set themes
    root.setThemes([
      am5themes_Dark.new(root)
    ]);

    // Create chart
    const chart = root.container.children.push(am5xy.XYChart.new(root, {
      panX: false,
      panY: false,
      wheelX: "none",
      wheelY: "none",
      pinchZoomX: false,
      paddingLeft: 0,
      paddingRight: 1
    }));

    // Add cursor
    const cursor = chart.set("cursor", am5xy.XYCursor.new(root, {}));
    cursor.lineY.set("visible", false);

    // Create axes
    const xRenderer = am5xy.AxisRendererX.new(root, {
      minGridDistance: 30,
      minorGridEnabled: true
    });
    xRenderer.labels.template.setAll({
      rotation: 0,
      centerY: am5.p100,
      centerX: am5.p50,
      paddingRight: 0
    });
    xRenderer.grid.template.setAll({ location: 1 });
    const xAxis = chart.xAxes.push(am5xy.CategoryAxis.new(root, {
      maxDeviation: 0.3,
      categoryField: "date",
      renderer: xRenderer,
      tooltip: am5.Tooltip.new(root, {})
    }));

    // yAxis.get("renderer").labels.template.setAll({
       const yRenderer = am5xy.AxisRendererY.new(root, {
      strokeOpacity: 0.1
    });

    const yAxis = chart.yAxes.push(am5xy.ValueAxis.new(root, {
      maxDeviation: 0.3,
      renderer: yRenderer,
      numberFormat: "#'%'",
      min: 0,
      max: 100,
      strictMinMax: true
    }));

    // Format y-axis labels to show percentage symbol
    yAxis.get("renderer").labels.template.setAll({
      text: "{value}%"
    });

    // Green series (communication)
    const greenSeries = chart.series.push(am5xy.ColumnSeries.new(root, {
      name: "Communication",
      xAxis: xAxis,
      yAxis: yAxis,
      valueYField: "communicationPercent",
      categoryXField: "date",
      stacked: true,
      fill: am5.color("#3ec46d"),
      stroke: am5.color("#3ec46d"),
      tooltip: am5.Tooltip.new(root, {
        labelText: "{categoryX}: {valueY}% Communication"
      })
    }));

    // Red series (non-communication)
    const redSeries = chart.series.push(am5xy.ColumnSeries.new(root, {
      name: "Non-Communication",
      xAxis: xAxis,
      yAxis: yAxis,
      valueYField: "nonCommunicationPercent",
      categoryXField: "date",
      stacked: true,
      fill: am5.color("#e74c3c"),
      stroke: am5.color("#e74c3c"),
      tooltip: am5.Tooltip.new(root, {
        labelText: "{categoryX}: {valueY}% Non-Communication"
      })
    }));

  // Set fixed pixel width for columns
  greenSeries.columns.template.setAll({ width: 30 });
  redSeries.columns.template.setAll({ width: 30 });

  // Store chart references for updates
  (chartDiv.current as any).chartRoot = root;
  (chartDiv.current as any).chartGreenSeries = greenSeries;
  (chartDiv.current as any).chartRedSeries = redSeries;
  (chartDiv.current as any).chartXAxis = xAxis;

  greenSeries.appear(1000);
  redSeries.appear(1000);
  chart.appear(1000, 100);

    return () => {
      root.dispose();
    };
  }, []);

  // Update chart data when connectivityData changes
  useEffect(() => {
    if (
      chartDiv.current &&
      (chartDiv.current as any).chartGreenSeries &&
      (chartDiv.current as any).chartRedSeries &&
      connectivityData.length > 0
    ) {
      const greenSeries = (chartDiv.current as any).chartGreenSeries;
      const redSeries = (chartDiv.current as any).chartRedSeries;
      const xAxis = (chartDiv.current as any).chartXAxis;

      // Clear existing data
      greenSeries.data.clear();
      redSeries.data.clear();
      xAxis.data.clear();

      // Set new data
      xAxis.data.setAll(connectivityData);
      greenSeries.data.setAll(connectivityData);
      redSeries.data.setAll(connectivityData);
    }
  }, [connectivityData]);

  // Handle chart resize when expanded state changes for connectivity widget
  useLayoutEffect(() => {
    if (chartDiv.current) {
      // Force a resize after expansion state change
      const resizeEvent = new Event('resize');
      window.dispatchEvent(resizeEvent);

      // Additional timeout to ensure DOM has updated
      setTimeout(() => {
        const resizeEvent = new Event('resize');
        window.dispatchEvent(resizeEvent);
      }, 100);
    }
  }, [isExpanded]);

  // Handle ESC key to close expanded widget for connectivity widget
  useLayoutEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (showCalendarModal) {
          setShowCalendarModal(false);
        } else if (isExpanded) {
          setIsExpanded(false);
          document.body.classList.remove('widget-expanded-body');
        }
      }
    };

    if (isExpanded || showCalendarModal) {
      document.addEventListener('keydown', handleEscKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isExpanded, showCalendarModal]);

  // Handle click outside to close calendar modal
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showCalendarModal && calendarModalRef.current && !calendarModalRef.current.contains(event.target as Node)) {
        setShowCalendarModal(false);
      }
    };

    if (showCalendarModal) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCalendarModal]);

const handleExpandClick = () => {
  setIsExpanded(prev => {
    const next = !prev;

    if (next) {
      document.body.classList.add('widget-expanded-body');
    } else {
      document.body.classList.remove('widget-expanded-body');
    }

    return next;
  });
};


  return (
    <>
      <style>{`
        .connectivity-calendar-modal::-webkit-scrollbar {
          width: 6px;
        }
        .connectivity-calendar-modal::-webkit-scrollbar-track {
          background: #22314a;
          border-radius: 3px;
        }
        .connectivity-calendar-modal::-webkit-scrollbar-thumb {
          background: #4a5463;
          border-radius: 3px;
        }
        .connectivity-calendar-modal::-webkit-scrollbar-thumb:hover {
          background: #5a6373;
        }
      `}</style>
      <div className={`widget connectivity-trend-widget ${isExpanded ? 'expanded' : ''}`}>
        <div className="widget-header conductivity">
          <div className="widget-title-row">
            <h3>Connectivity Trend</h3>
            <div className="widget-actions">
              <button className="widget-action-btn expand-btn" title={isExpanded ? "Close" : "Expand"} onClick={handleExpandClick}>
                {isExpanded ? (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                ) : (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </button>
            </div>
          </div>
          <div className="widget-controls-row">
            <div className="alerts-filter-buttons" style={{ position: 'relative' }}>
              {timeRangeOptions.map((option) => (
                <button
                  key={option}
                  className={`alert-filter-btn ${selectedTimeRange === option || (option === 'Select Date Range' && selectedTimeRange === 'Select Date Range' && startDate && endDate) ? 'active' : ''}`}
                  onClick={() => handleTimeRangeSelect(option)}
                  style={{
                    padding: '8px 16px',
                    margin: '0 4px',
                    background: selectedTimeRange === option || (option === 'Select Date Range' && selectedTimeRange === 'Select Date Range' && startDate && endDate) ? '#4a5463' : 'transparent',
                    color: '#e0e0e0',
                    border: '1px solid #4a5463',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  {option === 'Select Date Range' ? (
                    <>
                      {selectedTimeRange === 'Select Date Range' && startDate && endDate
                        ? `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`
                        : option}
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        style={{ marginLeft: '4px' }}
                      >
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" stroke="currentColor" strokeWidth="2" />
                        <line x1="16" y1="2" x2="16" y2="6" stroke="currentColor" strokeWidth="2" />
                        <line x1="8" y1="2" x2="8" y2="6" stroke="currentColor" strokeWidth="2" />
                        <line x1="3" y1="10" x2="21" y2="10" stroke="currentColor" strokeWidth="2" />
                      </svg>
                    </>
                  ) : (
                    option
                  )}
                </button>
              ))}

              {/* Calendar Modal - only shown when Select date range is clicked */}
              {showCalendarModal && (
                <div
                  className='calendar-modal-container connectivity-calendar-modal'
                  ref={calendarModalRef}
                  style={{
                    position: 'absolute',
                    top: '258%',
                    left: '0',
                    right: 'auto',
                    background: '#22314a',
                    border: '1px solid #4a5463',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                    zIndex: 9999,
                    minWidth: '350px',
                    marginTop: '-49px'
                  }}
                >
                  <div style={{ padding: '16px' }}>
                    <div className="calendar-section">
                      <div className="calendar-header" style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '12px'
                      }}>
                        <button
                          className="calendar-nav-btn"
                          onClick={() => navigateMonth(-1)}
                          style={{
                            background: 'transparent',
                            border: '1px solid #4a5463',
                            color: '#e0e0e0',
                            borderRadius: '4px',
                            width: '32px',
                            height: '32px',
                            cursor: 'pointer'
                          }}
                        >‹</button>
                        <span style={{ color: '#e0e0e0', fontSize: '14px', fontWeight: 'bold' }}>
                          {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                        </span>
                        <button
                          className="calendar-nav-btn"
                          onClick={() => navigateMonth(1)}
                          style={{
                            background: 'transparent',
                            border: '1px solid #4a5463',
                            color: '#e0e0e0',
                            borderRadius: '4px',
                            width: '32px',
                            height: '32px',
                            cursor: 'pointer'
                          }}
                        >›</button>
                      </div>

                      <div className="calendar-days-header" style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(7, 1fr)',
                        gap: '4px',
                        marginBottom: '8px'
                      }}>
                        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                          <div key={day} style={{
                            padding: '8px 4px',
                            textAlign: 'center',
                            fontSize: '12px',
                            fontWeight: 'bold',
                            color: '#bfc9db'
                          }}>{day}</div>
                        ))}
                      </div>

                      <div className="calendar-days-grid" style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(7, 1fr)',
                        gap: '4px',
                        marginBottom: '12px'
                      }}>
                        {getDaysInMonth(currentMonth).map((date, index) => {
                          const isCurrentMonth = date.getMonth() === currentMonth.getMonth();
                          const isToday = date.toDateString() === new Date().toDateString();
                          const isStart = isStartDate(date);
                          const isEnd = isEndDate(date);
                          const isInRange = isDateInRange(date);

                          let dayStyle = {
                            padding: '8px 4px',
                            textAlign: 'center' as const,
                            fontSize: '12px',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: isCurrentMonth ? 'pointer' : 'default',
                            background: 'transparent',
                            color: isCurrentMonth ? '#e0e0e0' : '#666',
                          };

                          if (isToday && isCurrentMonth) {
                            dayStyle.background = 'rgba(74, 84, 99, 0.5)';
                            dayStyle.color = '#fff';
                          }

                          if (isStart || isEnd) {
                            dayStyle.background = '#4a5463';
                            dayStyle.color = '#fff';
                          } else if (isInRange && isCurrentMonth) {
                            dayStyle.background = 'rgba(74, 84, 99, 0.3)';
                            dayStyle.color = '#e0e0e0';
                          }

                          return (
                            <button
                              key={index}
                              style={dayStyle}
                              onClick={() => isCurrentMonth && handleDateClick(date)}
                              disabled={!isCurrentMonth}
                            >
                              {date.getDate()}
                            </button>
                          );
                        })}
                      </div>

                      {(startDate || endDate) && (
                        <div className="date-selection-info" style={{
                          marginTop: '12px',
                          padding: '8px',
                          background: 'rgba(74, 84, 99, 0.2)',
                          borderRadius: '4px'
                        }}>
                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            fontSize: '12px',
                            color: '#bfc9db'
                          }}>
                            <span>Start: {startDate ? startDate.toLocaleDateString() : 'Not selected'}</span>
                            <span>End: {endDate ? endDate.toLocaleDateString() : 'Not selected'}</span>
                          </div>
                        </div>
                      )}

                      <div className="calendar-footer" style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: '8px',
                        marginTop: '16px'
                      }}>
                        <button
                          className="reset-btn"
                          onClick={resetDateSelection}
                          style={{
                            background: 'transparent',
                            border: '1px solid #4a5463',
                            color: '#e0e0e0',
                            borderRadius: '4px',
                            padding: '8px 16px',
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          Reset
                        </button>
                        <button
                          className={`apply-btn ${(!startDate || !endDate) ? 'disabled' : ''}`}
                          onClick={handleDateRangeApply}
                          disabled={!startDate || !endDate}
                          style={{
                            background: (!startDate || !endDate) ? 'rgba(74, 84, 99, 0.5)' : '#4a5463',
                            border: '1px solid #4a5463',
                            color: (!startDate || !endDate) ? 'rgba(224, 224, 224, 0.5)' : '#e0e0e0',
                            borderRadius: '4px',
                            padding: '8px 16px',
                            cursor: (!startDate || !endDate) ? 'not-allowed' : 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          Apply Custom Range
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="widget-content">
          {/* Loading state */}
          {loading && (
            <div className="loading-state" style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '250px',
              color: '#bfc9db',
              fontSize: '14px'
            }}>
              Loading connectivity data...
            </div>
          )}

          {/* No data state */}
          {!loading && connectivityData.length === 0 && (
            <div className="no-data-state" style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '250px',
              color: '#bfc9db',
              fontSize: '14px'
            }}>
              No data found for selected date range
            </div>
          )}

          {/* Chart - always rendered but hidden when loading or no data */}
          <div
            ref={chartDiv}
            style={{
              width: "100%",
              height: "250px",
              display: (loading || connectivityData.length === 0) ? 'none' : 'block'
            }}
          ></div>
        </div>
      </div>
    </>
  );
};

export default AssetSummaryDetails;
